import { CustomizationItemInfo } from "../customization/customization.data";

export type PropertiesOnly<T> = { [K in keyof T as (T[K] extends Function ? never : K)]?: T[K] };
export type PluginResponse<T> = {
    error: number;
    errorMessage?: string;
    data: T;
}

export class PluginItemInfo<T> {
    prev: T;
    current: T;

    get isChanged(): boolean {
        return this.prev !== this.current;
    }

    get isValid(): boolean {
        return this.current ? true : false;
    }

    constructor(defaultData: T) {
        this.prev = this.current = defaultData;
    }

    setAll(v: T): void {
        this.prev = this.current = v;
    }

    set(v: T): void {
        this.current = v;
    }

    reset(): void {
        this.current = this.prev;
    }

    export(): T {
        return this.isChanged ? this.current : undefined;
    }
}

export interface IPluginDataProvider {
    name: string;
    config: {
        authType: AuthOptionType;
        tenantUrl: string;
        username?: string;
        userCredential?: string;
        clientId?: string;
        clientSecret?: string;
    };
    iadeaCareAccessInfo: { accessKeyID: string, accessKeyRemark: string, isAccessKeyEnabled: boolean };
}

export interface IPluginDataCalendar {
    locale: string;
    pin: number;
    background: string;
    logo: string;
    fontsizeRatio?: number;
    theme: {
        foreground?: string;
        availableColor?: string;
        busyColor?: string;
        timeline?: {
            bgColor?: string;
            futureEventTimeBlockColor?: string;
            currentEventTimeBlockColor?: string;
            expiredEventTimeBlockColor?: string;
        }
    };
    calendar: {
        enableOnsiteBook?: boolean;
        enableOnsiteExtend?: boolean;
        enableOnsiteCancelOrStop?: boolean;
        enableOnsiteAutoCheckin?: boolean;
        enableFutureEventBook?: boolean;
        enableFutureEventCancel?: boolean;
        enableUserList?: boolean;
        userDisplayOption?: UserDisplayOptionType;
        hideTimeline?: boolean;
        showEventSubject?: boolean;
        reservedDisplayNameForOnSiteBook?: string;
    };
    lightbar: {
        activate?: boolean;
        brightness?: number;
        available: {
            color?: string;
            mode?: string;
        },  
        busy: {
            color?: string;
            mode?: string;
        }
    };
    deskOffice: {
        upcomingAvailabilityWindow?: number;
        enableOnsiteBook?: boolean;
        enableOnsiteCancelOrStop?: boolean;
        enableOnsiteAutoCheckin?: boolean;
        reservedDisplayNameForOnSiteBook?: string;
        theme?: {
            foreground?: string;
            availableColor?: string;
            busyColor?: string;
            borderLineWeight?: number;
        }
    }
}

export interface IPluginDataPairing {
    spaceID?: string;
    spaceShortID?: string;
    spaceName?: string;
    type?: SpaceType;
    emailAddress?: string;
    capacity?: number;
    devices: {
        deviceID: string;
        deviceName?: string;
        isPaired: boolean;
        pairingDateTime: Date;
    }[]
}

export const AuthOption = {
    BASIC: 'BASIC',
    CLIENT_CREDENTIAL: 'CLIENT_CREDENTIAL'
} as const;
export type AuthOptionType = typeof AuthOption[keyof typeof AuthOption];
export class PluginProviderInfo {
    authType: AuthOptionType;
    tenantUrl: PluginItemInfo<string>;
    username?: PluginItemInfo<string>;
    userCredential?: PluginItemInfo<string>;
    clientId?: PluginItemInfo<string>;
    clientSecret?: PluginItemInfo<string>;

    constructor(raw?: Partial<IPluginDataProvider>) {
        this.tenantUrl = new PluginItemInfo<string>(raw?.config?.tenantUrl || '');
        this.username = new PluginItemInfo<string>(raw?.config?.username || '');
        this.userCredential = new PluginItemInfo<string>(raw?.config?.userCredential || '');
        this.clientId = new PluginItemInfo<string>(raw?.config?.clientId || '');
        this.clientSecret = new PluginItemInfo<string>(raw?.config?.clientSecret || '');
        this.authType = raw?.config?.authType || AuthOption.CLIENT_CREDENTIAL;
    }

    isValid(): boolean {
        return this.tenantUrl.isValid && (this.authType === AuthOption.BASIC ? this.username.isValid && this.userCredential.isValid : this.clientId.isValid && this.clientSecret.isValid);
    }

    isChanged(): boolean {
        return this.tenantUrl.isChanged || (this.authType === AuthOption.BASIC && (this.username?.isChanged || this.userCredential?.isChanged)) || (this.authType === AuthOption.CLIENT_CREDENTIAL && (this.clientId?.isChanged || this.clientSecret?.isChanged));
    }

    reset(): void {
        this.tenantUrl.reset();
        if (this.authType === AuthOption.BASIC) {
            this.username.reset();
            this.userCredential.reset();
        }
        else if (this.authType === AuthOption.CLIENT_CREDENTIAL) {
            this.clientId.reset();
            this.clientSecret.reset();
        }
    }

    export(): Partial<IPluginDataProvider> {
        return {
            config: Object.assign({
                authType: this.authType,
                tenantUrl: this.tenantUrl.export()
            }, this.authType === AuthOption.BASIC ? {
                username: this.username.export(),
                userCredential: this.userCredential.export()
            } : {
                clientId: this.clientId.export(),
                clientSecret: this.clientSecret.export()
            })
        };
    }
}

export class EmsPluginProviderInfo extends PluginProviderInfo {
    clientId: PluginItemInfo<string>
    clientSecret: PluginItemInfo<string>;

    constructor(raw?: Partial<IPluginDataProvider>) {
        super(raw);
        this.clientId = new PluginItemInfo<string>(raw?.config?.clientId || '');
        this.clientSecret = new PluginItemInfo<string>(raw?.config?.clientSecret || '');
    }

    isValid(): boolean {
        return super.isValid() && this.clientId.isValid && this.clientSecret.isValid;
    }

    isChanged(): boolean {
        return super.isChanged() ||
            this.clientId.isChanged ||
            this.clientSecret.isChanged;
    }

    reset(): void {
        super.reset();
        this.clientId.reset();
        this.clientSecret.reset();
    }

    export(): Partial<IPluginDataProvider> {
        const baseExport = super.export();
        return {
            config: {
                ...baseExport.config,
                clientId: this.clientId.export(),
                clientSecret: this.clientSecret.export()
            }
        }
    }
}

export type ProviderInfo = PluginProviderInfo | EmsPluginProviderInfo;
export function createPluginProviderInfo(raw: Partial<IPluginDataProvider>): ProviderInfo {
    const pluginType = raw?.name?.toLowerCase() || 'svcnow';
    switch (pluginType) {
        case 'ems':
            // If it's EMS, use the extended class
            return new EmsPluginProviderInfo(raw);

        case 'svcnow':
        default:
            // For SVCNow and any unknown brands, use the base class
            return new PluginProviderInfo(raw);
    }
}

export const UserDisplayOption = {
    USER_ID: 'USER_ID',
    USER_NAME: 'USER_NAME',
    ALL: 'ALL'
} as const;
export type UserDisplayOptionType = typeof UserDisplayOption[keyof typeof UserDisplayOption];

export class PluginCalendarInfo {
    locale: PluginItemInfo<string>;
    pin: PluginItemInfo<number>;
    background: CustomizationItemInfo;
    logo: CustomizationItemInfo;
    fontsizeRatio: PluginItemInfo<number>;
    theme: {
        foreground?: PluginItemInfo<string>;
        availableColor?: PluginItemInfo<string>;
        busyColor?: PluginItemInfo<string>;
        timeline?: {
            bgColor?: PluginItemInfo<string>;
            futureEventTimeBlockColor?: PluginItemInfo<string>;
            currentEventTimeBlockColor?: PluginItemInfo<string>;
            expiredEventTimeBlockColor?: PluginItemInfo<string>;
        }
    };
    calendar: {
        enableOnsiteBook?: PluginItemInfo<boolean>;
        enableOnsiteExtend?: PluginItemInfo<boolean>;
        enableOnsiteCancelOrStop?: PluginItemInfo<boolean>;
        enableFutureEventBook?: PluginItemInfo<boolean>;
        enableOnsiteAutoCheckin?: PluginItemInfo<boolean>;
        enableFutureEventCancel?: PluginItemInfo<boolean>;
        enableUserList?: PluginItemInfo<boolean>;
        userDisplayOption?: PluginItemInfo<UserDisplayOptionType>;
        showTimeline?: PluginItemInfo<boolean>;
        showEventSubject?: PluginItemInfo<boolean>;
        reservedDisplayNameForOnSiteBook?: PluginItemInfo<string>;
    };
    lightbar: {
        activate?: PluginItemInfo<boolean>;
        brightness?:  PluginItemInfo<number>;
        available: {
            color?: PluginItemInfo<string>;
            mode?: PluginItemInfo<string>;
        },
        busy: {
            color?: PluginItemInfo<string>;
            mode?: PluginItemInfo<string>;
        }
    };
    deskOffice: {
        upcomingAvailabilityWindow?: PluginItemInfo<number>;
        enableOnsiteBook?: PluginItemInfo<boolean>;
        enableOnsiteCancelOrStop?: PluginItemInfo<boolean>;
        enableOnsiteAutoCheckin?: PluginItemInfo<boolean>;
        reservedDisplayNameForOnSiteBook?: PluginItemInfo<string>;
        theme?: {
            foreground?: PluginItemInfo<string>;
            availableColor?: PluginItemInfo<string>;
            busyColor?: PluginItemInfo<string>;
            borderLineWeight?: PluginItemInfo<number>;
        }
    }

    constructor(imgBaseUrl?: string, accountID?: string, token?: string, raw?: Partial<IPluginDataCalendar>) {
        this.locale = new PluginItemInfo(raw?.locale || 'en-us');
        this.pin = new PluginItemInfo(raw?.pin || 1688);
        this.fontsizeRatio = new PluginItemInfo(raw?.fontsizeRatio || 1);
        this.background = new CustomizationItemInfo();
        this.logo = new CustomizationItemInfo();
        if (imgBaseUrl) {
            this.background.reset(`${imgBaseUrl}/${accountID}/${raw?.background}?token=${token}&nounce=${Date.now()}`);
            this.logo.reset(`${imgBaseUrl}/${accountID}/${raw?.logo}?token=${token}&nounce=${Date.now()}`);
        }

        this.theme = {
            foreground: new PluginItemInfo(raw?.theme?.foreground || '#000000'),
            availableColor: new PluginItemInfo(raw?.theme?.availableColor || '#1cbf92'),
            busyColor: new PluginItemInfo(raw?.theme?.busyColor || '#bb2323'),
            timeline: {
                bgColor: new PluginItemInfo(raw?.theme?.timeline?.bgColor || '#d3d3d3'),
                futureEventTimeBlockColor: new PluginItemInfo(raw?.theme?.timeline?.futureEventTimeBlockColor || '#c96565'),
                currentEventTimeBlockColor: new PluginItemInfo(raw?.theme?.timeline?.currentEventTimeBlockColor || '#d72c2c'),
                expiredEventTimeBlockColor: new PluginItemInfo(raw?.theme?.timeline?.expiredEventTimeBlockColor || '#6c757d')
            }
        };
        this.calendar = {
            enableOnsiteBook: new PluginItemInfo(raw?.calendar?.enableOnsiteBook ?? true),
            enableOnsiteExtend: new PluginItemInfo(raw?.calendar?.enableOnsiteExtend ?? true),
            enableOnsiteCancelOrStop: new PluginItemInfo(raw?.calendar?.enableOnsiteCancelOrStop ?? true),
            enableOnsiteAutoCheckin: new PluginItemInfo(raw?.calendar?.enableOnsiteAutoCheckin ?? false),
            enableFutureEventBook: new PluginItemInfo(raw?.calendar?.enableFutureEventBook ?? true),
            enableFutureEventCancel: new PluginItemInfo(raw?.calendar?.enableFutureEventCancel ?? true),
            enableUserList: new PluginItemInfo(raw?.calendar?.enableUserList ?? false),
            userDisplayOption: new PluginItemInfo(raw?.calendar?.userDisplayOption ?? UserDisplayOption.USER_NAME),
            showTimeline: new PluginItemInfo(raw?.calendar?.hideTimeline !== undefined ? !raw.calendar.hideTimeline : true),
            showEventSubject: new PluginItemInfo(raw?.calendar?.showEventSubject ?? true),
            reservedDisplayNameForOnSiteBook: new PluginItemInfo(raw?.calendar?.reservedDisplayNameForOnSiteBook || '')
        };
        this.lightbar = {
            activate: new PluginItemInfo(raw?.lightbar?.activate ?? true),
            brightness: new PluginItemInfo(raw?.lightbar?.brightness ?? 100),
            available: {
                color: new PluginItemInfo(raw?.lightbar?.available?.color || '#00ff00'),
                mode: new PluginItemInfo(raw?.lightbar?.available?.mode || 'on')
            },
            busy: {
                color: new PluginItemInfo(raw?.lightbar?.busy?.color || '#ff0000'),
                mode: new PluginItemInfo(raw?.lightbar?.busy?.mode || 'on')
            }
        };
        this.deskOffice = {
            upcomingAvailabilityWindow: new PluginItemInfo(raw?.deskOffice?.upcomingAvailabilityWindow ?? 20),
            enableOnsiteBook: new PluginItemInfo(raw?.deskOffice?.enableOnsiteBook ?? true),
            enableOnsiteCancelOrStop: new PluginItemInfo(raw?.deskOffice?.enableOnsiteCancelOrStop ?? true),
            enableOnsiteAutoCheckin: new PluginItemInfo(raw?.deskOffice?.enableOnsiteAutoCheckin ?? false),
            reservedDisplayNameForOnSiteBook: new PluginItemInfo(raw?.deskOffice?.reservedDisplayNameForOnSiteBook || 'On-site booking'),
            theme: {
                foreground: new PluginItemInfo(raw?.deskOffice?.theme?.foreground || '#000000'),
                availableColor: new PluginItemInfo(raw?.deskOffice?.theme?.availableColor || '#1cbf9280'),
                busyColor: new PluginItemInfo(raw?.deskOffice?.theme?.busyColor || '#bb232380'),
                borderLineWeight: new PluginItemInfo(raw?.deskOffice?.theme?.borderLineWeight ?? 3)
            }
        };
    }

    isValid(): boolean {
        return true;
    }

    isChanged(): boolean {
        return this.locale.isChanged ||
            this.pin.isChanged ||
            this.background.isChanged() ||
            this.logo.isChanged() ||
            this.fontsizeRatio.isChanged ||
            this.theme.foreground.isChanged ||
            this.theme.availableColor.isChanged ||
            this.theme.busyColor.isChanged ||
            this.theme.timeline.futureEventTimeBlockColor.isChanged ||
            this.theme.timeline.currentEventTimeBlockColor.isChanged ||
            this.theme.timeline.expiredEventTimeBlockColor.isChanged ||
            this.calendar.enableFutureEventBook.isChanged ||
            this.calendar.enableFutureEventCancel.isChanged ||
            this.calendar.enableOnsiteBook.isChanged ||
            this.calendar.enableOnsiteExtend.isChanged ||
            this.calendar.enableOnsiteCancelOrStop.isChanged ||
            this.calendar.enableOnsiteAutoCheckin.isChanged ||
            this.calendar.enableUserList.isChanged ||
            this.calendar.userDisplayOption.isChanged ||
            this.calendar.showTimeline.isChanged ||
            this.calendar.showEventSubject.isChanged ||
            this.calendar.reservedDisplayNameForOnSiteBook.isChanged ||
            this.lightbar.activate.isChanged ||
            this.lightbar.brightness.isChanged ||
            this.lightbar.available.color.isChanged ||
            this.lightbar.available.mode.isChanged ||
            this.lightbar.busy.color.isChanged ||
            this.lightbar.busy.mode.isChanged ||
            this.deskOffice.upcomingAvailabilityWindow.isChanged ||
            this.deskOffice.enableOnsiteBook.isChanged ||
            this.deskOffice.enableOnsiteCancelOrStop.isChanged ||
            this.deskOffice.enableOnsiteAutoCheckin.isChanged ||
            this.deskOffice.reservedDisplayNameForOnSiteBook.isChanged ||
            this.deskOffice.theme.foreground.isChanged ||
            this.deskOffice.theme.availableColor.isChanged ||
            this.deskOffice.theme.busyColor.isChanged ||
            this.deskOffice.theme.borderLineWeight.isChanged;
    }

    reset(): void {
        this.locale.reset();
        this.pin.reset();
        this.background.reset();
        this.logo.reset();
        this.fontsizeRatio.reset();
        this.theme.foreground.reset();
        this.theme.availableColor.reset();
        this.theme.busyColor.reset();
        this.theme.timeline.bgColor.reset();
        this.theme.timeline.futureEventTimeBlockColor.reset();
        this.theme.timeline.currentEventTimeBlockColor.reset();
        this.theme.timeline.expiredEventTimeBlockColor.reset();
        this.calendar.enableFutureEventBook.reset();
        this.calendar.enableFutureEventCancel.reset();
        this.calendar.enableOnsiteBook.reset();
        this.calendar.enableOnsiteExtend.reset();
        this.calendar.enableOnsiteCancelOrStop.reset();
        this.calendar.enableOnsiteAutoCheckin.reset();
        this.calendar.enableUserList.reset();
        this.calendar.userDisplayOption.reset();
        this.calendar.showTimeline.reset();
        this.calendar.showEventSubject.reset();
        this.calendar.reservedDisplayNameForOnSiteBook.reset();
        this.lightbar.activate.reset();
        this.lightbar.brightness.reset();
        this.lightbar.available.color.reset();
        this.lightbar.available.mode.reset();
        this.lightbar.busy.color.reset();
        this.lightbar.busy.mode.reset();
        this.deskOffice.upcomingAvailabilityWindow.reset();
        this.deskOffice.enableOnsiteBook.reset();
        this.deskOffice.enableOnsiteCancelOrStop.reset();
        this.deskOffice.enableOnsiteAutoCheckin.reset();
        this.deskOffice.reservedDisplayNameForOnSiteBook.reset();
        this.deskOffice.theme.foreground.reset();
        this.deskOffice.theme.availableColor.reset();
        this.deskOffice.theme.busyColor.reset();
        this.deskOffice.theme.borderLineWeight.reset();
    }

    export(): Partial<IPluginDataCalendar> {
        return {
            locale: this.locale.export(),
            pin: this.pin.export(),
            fontsizeRatio: this.fontsizeRatio.export(),
            //background: this.background.name,
            //logo: this.logo.name,
            theme: {
                foreground: this.theme.foreground.export(),
                availableColor: this.theme.availableColor.export(),
                busyColor: this.theme.busyColor.export(),
                timeline: {
                    bgColor: this.theme.timeline.bgColor.export(),
                    futureEventTimeBlockColor: this.theme.timeline.futureEventTimeBlockColor.export(),
                    currentEventTimeBlockColor: this.theme.timeline.currentEventTimeBlockColor.export(),
                    expiredEventTimeBlockColor: this.theme.timeline.expiredEventTimeBlockColor.export()
                }
            },
            calendar: {
                enableFutureEventBook: this.calendar.enableFutureEventBook.export(),
                enableFutureEventCancel: this.calendar.enableFutureEventCancel.export(),
                enableOnsiteBook: this.calendar.enableOnsiteBook.export(),
                enableOnsiteExtend: this.calendar.enableOnsiteExtend.export(),
                enableOnsiteCancelOrStop: this.calendar.enableOnsiteCancelOrStop.export(),
                enableOnsiteAutoCheckin: this.calendar.enableOnsiteAutoCheckin.export(),
                enableUserList: this.calendar.enableUserList.export(),
                userDisplayOption: this.calendar.userDisplayOption.export(),
                hideTimeline: this.calendar.showTimeline.export() !== undefined ? !this.calendar.showTimeline.export() : undefined,
                showEventSubject: this.calendar.showEventSubject.export(),
                reservedDisplayNameForOnSiteBook: this.calendar.reservedDisplayNameForOnSiteBook.export()
            },
            lightbar: {
                activate: this.lightbar.activate.export(),
                brightness: this.lightbar.brightness.export(),
                available: {
                    color: this.lightbar.available.color.export(),
                    mode: this.lightbar.available.mode.export()
                },
                busy: {
                    color: this.lightbar.busy.color.export(),
                    mode: this.lightbar.busy.mode.export()
                }
            },
            deskOffice: {
                upcomingAvailabilityWindow: this.deskOffice.upcomingAvailabilityWindow.export(),
                enableOnsiteBook: this.deskOffice.enableOnsiteBook.export(),
                enableOnsiteCancelOrStop: this.deskOffice.enableOnsiteCancelOrStop.export(),
                enableOnsiteAutoCheckin: this.deskOffice.enableOnsiteAutoCheckin.export(),
                reservedDisplayNameForOnSiteBook: this.deskOffice.reservedDisplayNameForOnSiteBook.export(),
                theme: {
                    foreground: this.deskOffice.theme.foreground.export(),
                    availableColor: this.deskOffice.theme.availableColor.export(),
                    busyColor: this.deskOffice.theme.busyColor.export(),
                    borderLineWeight: this.deskOffice.theme.borderLineWeight.export()
                }
            }
        }
    }
}

export type SpaceType = 'room' | 'zone';
export class PluginSpaceInfo {
    spaceID?: string;
    spaceShortID?: string;
    spaceName?: string;
    type?: SpaceType;
    reservableModuleID: string;
    roomRecordType?: string;
    emailAddress?: string;
    capacity?: number;
    officeHour?: { dayStart: string, dayEnd: string, dayHalf: string };

    constructor(raw?: Partial<PropertiesOnly<PluginSpaceInfo>>) {
        this.spaceID = raw?.spaceID || '';
        this.spaceShortID = this.spaceID.substring(0, 5);
        this.spaceName = raw.spaceName;
        this.type = raw.type;
        this.emailAddress = raw.emailAddress;
        this.capacity = raw.capacity;
        this.reservableModuleID = raw.reservableModuleID;
        this.roomRecordType = raw.roomRecordType;
        this.officeHour = raw.officeHour;
    }
}

export type PluginPairingRequestInfo = Partial<PluginSpaceInfo> & {
    devices: {
        deviceID: string;
        deviceName?: string;
        isPaired: boolean;
        pairingDateTime: Date;
    }[]
};